/**
 * Analytics Event Enricher
 *
 * Automatically attaches context to every analytics event:
 * - source (platform/plugin)
 * - timestamp, session_id, user_id, anonymous_id
 * - page_path, page_name, is_logged_in
 * - user_agent, screen_resolution, language
 *
 * @module @ideoz/core/analytics/enricher
 */

import { getPlatformSource, isWebBuild } from '../utils/environment';
import type { AnalyticsEvent, AnalyticsEventProperties, GetReduxState } from './analytics.types';
import { analyticsConfig } from './analytics.config';

// ==================== Session Management ====================

let sessionId: string | null = null;
let anonymousId: string | null = null;

/**
 * Generate a UUID v4 (crypto-safe when available, fallback otherwise).
 */
function generateUUID(): string {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  // Fallback for older browsers
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

/**
 * Get or create a session ID.
 * Web: persisted in sessionStorage. Plugin: in-memory.
 */
function getSessionId(): string {
  if (sessionId) return sessionId;

  if (isWebBuild()) {
    try {
      const stored = window.sessionStorage.getItem('analytics_session_id');
      if (stored) {
        sessionId = stored;
        return sessionId;
      }
    } catch {
      // sessionStorage not available — fall through
    }
  }

  sessionId = generateUUID();

  if (isWebBuild()) {
    try {
      window.sessionStorage.setItem('analytics_session_id', sessionId);
    } catch {
      // Ignore
    }
  }

  return sessionId;
}

/**
 * Get or create an anonymous ID for non-authenticated users.
 */
function getAnonymousId(): string {
  if (anonymousId) return anonymousId;

  if (isWebBuild()) {
    try {
      const stored = window.sessionStorage.getItem('analytics_anonymous_id');
      if (stored) {
        anonymousId = stored;
        return anonymousId;
      }
    } catch {
      // Fall through
    }
  }

  anonymousId = generateUUID();

  if (isWebBuild()) {
    try {
      window.sessionStorage.setItem('analytics_anonymous_id', anonymousId);
    } catch {
      // Ignore
    }
  }

  return anonymousId;
}

// ==================== Page Name Resolution ====================

/**
 * Match a pathname against the pageViews config.
 * Supports exact matches and simple patterns (:param, *).
 */
function resolvePageName(pathname: string): string | undefined {
  const config = analyticsConfig.pageViews;

  // 1. Exact match
  if (config[pathname]) {
    return config[pathname].page_name;
  }

  // 2. Pattern match
  for (const pattern of Object.keys(config)) {
    if (matchRoute(pattern, pathname)) {
      return config[pattern].page_name;
    }
  }

  return undefined;
}

/**
 * Simple route pattern matcher.
 * Supports :param segments and trailing *.
 */
function matchRoute(pattern: string, pathname: string): boolean {
  const patternParts = pattern.split('/');
  const pathParts = pathname.split('/');

  for (let i = 0; i < patternParts.length; i++) {
    const pp = patternParts[i];

    if (pp === '*') {
      return true; // Wildcard matches everything from here
    }

    if (pp.startsWith(':')) {
      // Dynamic segment — matches anything except empty
      if (!pathParts[i]) return false;
      continue;
    }

    if (pp !== pathParts[i]) {
      return false;
    }
  }

  return patternParts.length === pathParts.length;
}

// ==================== Store Accessor ====================

let getReduxState: GetReduxState | null = null;

/**
 * Register the Redux state accessor. Called once during init.
 */
export function setReduxStateAccessor(accessor: GetReduxState): void {
  getReduxState = accessor;
}

/**
 * Check if the current user is logged in (from Redux state).
 * Returns false if state is not yet initialized.
 */
export function isUserLoggedIn(): boolean {
  if (!getReduxState) return false;
  try {
    const state = getReduxState();
    const appState = (state.app ?? {}) as Record<string, unknown>;
    return appState.isLoggedIn === true;
  } catch {
    return false;
  }
}

// ==================== Enricher ====================

/**
 * Enrich a raw event with full context.
 * This is called for EVERY event before it enters the queue.
 */
export function enrichEvent(
  eventName: string,
  properties: Record<string, unknown> = {},
): AnalyticsEvent {
  const state = getReduxState ? getReduxState() : {};
  const appState = (state.app ?? {}) as Record<string, unknown>;
  const user = (appState.user ?? {}) as Record<string, unknown>;
  const isLoggedIn = appState.isLoggedIn === true;
  const userId = isLoggedIn ? (user.id as string | undefined) : undefined;

  // Determine current path
  let pagePath: string | undefined;
  if (isWebBuild() && typeof window !== 'undefined') {
    pagePath = window.location.pathname;
  } else {
    pagePath = (appState.currentPage as string) ?? undefined;
  }

  const pageName = pagePath ? resolvePageName(pagePath) : undefined;
  const projectId = (appState.currentProjectId as string | null) ?? undefined;

  // Build enriched properties
  const enrichedProperties: AnalyticsEventProperties = {
    page_path: pagePath,
    page_name: pageName,
    is_logged_in: isLoggedIn,
    ...(projectId ? { project_id: projectId } : {}),
    user_agent: typeof navigator !== 'undefined' ? navigator.userAgent : undefined,
    screen_resolution:
      typeof window !== 'undefined' && window.screen
        ? `${window.screen.width}x${window.screen.height}`
        : undefined,
    language: typeof navigator !== 'undefined' ? navigator.language : undefined,
    ...properties,
  };

  const event: AnalyticsEvent = {
    event_name: eventName,
    timestamp: new Date().toISOString(),
    source: getPlatformSource(),
    session_id: getSessionId(),
    ...(userId ? { user_id: userId } : { anonymous_id: getAnonymousId() }),
    properties: enrichedProperties,
  };

  return event;
}
